import type { GeckoBinnedDataBuffer } from 'features/dataLayers/geckoBinnedDataBuffer'
import { runWebWorker } from 'features/webWorkerApi/runWebWorker'

import CreateSparseBinCsvWorker from './createSparseBinCsv.worker?worker'

type DuckDbInstance = InstanceType<
  typeof import('@duckdb/duckdb-wasm').AsyncDuckDB
>

/**
 * The name of the parquet file uploaded onto the generated layer's own CDF
 * entry.
 */
export function getGeneratedCdfParquetFileName(cdfId: string): string {
  const fileKey = cdfId.replace(/[^a-zA-Z0-9_-]/g, '_')
  return `generated-cdf-${fileKey}.parquet`
}

/**
 * The generated layer's `_parquet_column_name`, which must match the column
 * written by `createCdfParquetBlob`.
 */
export function getGeneratedCdfParquetColumnName(
  cdfId: string | undefined
): string {
  const sanitized = (cdfId ?? '').replace(/[^a-zA-Z0-9_]/g, '_')
  return sanitized || 'value'
}

/**
 * Build a parquet blob of sparse (x, y, value) bin rows matching the schema
 * that `useCdfDataGenerator` queries on component-dataset parquet files.
 */
export async function createCdfParquetBlob(
  db: DuckDbInstance,
  binnedDataBuffer: Pick<GeckoBinnedDataBuffer, 'values' | '_xBinCount'>,
  columnName: string,
  uniqueKey: string
): Promise<Blob> {
  const workerValues = new Float32Array(binnedDataBuffer.values)
  const csvChunks = await runWebWorker<string[]>(
    new CreateSparseBinCsvWorker(),
    { values: workerValues, xBinCount: binnedDataBuffer._xBinCount },
    [workerValues.buffer]
  )

  const fileKey = uniqueKey.replace(/[^a-zA-Z0-9_-]/g, '_')
  const csvFileName = `generated-cdf-${fileKey}.csv`
  const parquetFileName = `generated-cdf-${fileKey}.parquet`

  await db.registerFileText(csvFileName, csvChunks.join(''))
  try {
    const conn = await db.connect()
    try {
      await conn.query(
        `COPY (
           SELECT *
           FROM read_csv(
             '${csvFileName}',
             header=false,
             columns={'x': 'INTEGER', 'y': 'INTEGER', '${columnName}': 'DOUBLE'}
           )
         ) TO '${parquetFileName}' (FORMAT PARQUET)`
      )
    } finally {
      await conn.close()
    }

    const parquetBuffer = await db.copyFileToBuffer(parquetFileName)
    return new Blob([parquetBuffer as BlobPart], {
      type: 'application/octet-stream',
    })
  } finally {
    await db.dropFiles([csvFileName, parquetFileName])
  }
}
